import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Search, Loader2, AlertCircle, Download, Settings, Edit2, Check, X } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useSuccessWareMasterFields, useEntityTypes, useFieldStats, useUpdateField } from '@/lib/hooks/useSuccessWareMasterFields';
import type { SuccessWareMasterField } from '@/lib/repositories/SuccessWareMasterFieldsRepository';

interface ColumnVisibility {
  entity: boolean;
  table: boolean;
  column: boolean;
  edgeFunction: boolean;
  fieldPath: boolean;
  jsonType: boolean;
  pushPull: boolean;
  description: boolean;
  imported: boolean;
  notes: boolean;
}

const SuccessWareFieldCatalogPage = () => {
  const { theme } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<string>('all');
  const [showImportedOnly, setShowImportedOnly] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ description: string; notes: string }>({ description: '', notes: '' });
  
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>({
    entity: true,
    table: true,
    column: true,
    edgeFunction: true,
    fieldPath: true,
    jsonType: false,
    pushPull: false,
    description: true,
    imported: true,
    notes: true
  });

  // Fetch data using React Query hooks
  const { data: fields = [], isLoading } = useSuccessWareMasterFields();
  const { data: entityTypes = [] } = useEntityTypes();
  const { data: stats = [] } = useFieldStats();
  const updateFieldMutation = useUpdateField();

  // Filter fields based on search, entity, and import status
  const filteredFields = useMemo(() => {
    return fields.filter(field => {
      const matchesSearch = 
        field.field_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        field.field_path.toLowerCase().includes(searchTerm.toLowerCase()) ||
        field.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        field.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        field.database_table?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesEntity = selectedEntity === 'all' || field.entity_type === selectedEntity;
      const matchesImportFilter = !showImportedOnly || field.is_imported;

      return matchesSearch && matchesEntity && matchesImportFilter;
    });
  }, [fields, searchTerm, selectedEntity, showImportedOnly]);

  const startEditing = (field: SuccessWareMasterField) => {
    setEditingField(field.id);
    setEditValues({
      description: field.description || '',
      notes: field.notes || ''
    });
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValues({ description: '', notes: '' });
  };

  const saveEditing = async (fieldId: string) => {
    try {
      await updateFieldMutation.mutateAsync({
        id: fieldId,
        updates: {
          description: editValues.description || null,
          notes: editValues.notes || null
        }
      });
      setEditingField(null);
    } catch (error) {
      console.error('Error updating field:', error);
      alert('Failed to update field');
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Entity Type',
      'Field Name',
      'Field Path',
      'Database Table',
      'Database Column',
      'Edge Function',
      'JSON Type',
      'Push/Pull',
      'Imported',
      'Description',
      'Notes'
    ];

    const rows = filteredFields.map(field => [
      field.entity_type,
      field.field_name,
      field.field_path,
      field.database_table || '',
      field.database_column || '',
      field.edge_function || '',
      field.json_type || '',
      field.push_pull || '',
      field.is_imported ? 'Yes' : 'No',
      field.description || '',
      field.notes || ''
    ]);

    const escapeCSV = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `successware-master-fields-${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleColumn = (column: keyof ColumnVisibility) => {
    setColumnVisibility(prev => ({ ...prev, [column]: !prev[column] }));
  };

  const totalFields = fields.length;
  const importedFields = fields.filter(f => f.is_imported).length;
  const totalEntities = entityTypes.length;

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm mb-4" style={{ color: theme.colors.textSecondary }}>
          <Link to="/integrations" className="hover:text-white" style={{ color: theme.colors.textSecondary }}>
            Integrations
          </Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/integrations/successware" className="hover:text-white" style={{ color: theme.colors.textSecondary }}>
            SuccessWare
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span style={{ color: theme.colors.textPrimary }}>Field Catalog</span>
        </div>
        <h1 className="text-3xl font-bold mb-2" style={{ color: theme.colors.textPrimary }}>
          SuccessWare Master Fields Catalog
        </h1>
        <p style={{ color: theme.colors.textSecondary }}>
          Complete field mapping documentation for {totalFields} fields across {totalEntities} entity types
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.colors.primaryColor }} />
        </div>
      ) : (
        <>
          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { value: totalFields, label: 'Total Fields' },
              { value: totalEntities, label: 'Entity Types' },
              { value: importedFields, label: 'Imported Fields' },
              { value: filteredFields.length, label: 'Filtered Results' }
            ].map((stat, idx) => (
              <div 
                key={idx}
                className="rounded-lg p-4"
                style={{ 
                  backgroundColor: theme.colors.bgCard,
                  border: `1px solid ${theme.colors.borderColor}`
                }}
              >
                <div className="text-2xl font-bold mb-1" style={{ color: theme.colors.textPrimary }}>
                  {stat.value}
                </div>
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Entity Statistics */}
          {stats.length > 0 && (
            <div 
              className="rounded-lg p-4 mb-6"
              style={{ 
                backgroundColor: theme.colors.bgCard,
                border: `1px solid ${theme.colors.borderColor}`
              }}
            >
              <h2 className="text-lg font-semibold mb-3" style={{ color: theme.colors.textPrimary }}>
                Fields by Entity Type
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {stats.map((stat) => (
                  <div 
                    key={stat.entity_type}
                    className="text-sm p-2 rounded"
                    style={{ backgroundColor: theme.colors.bgHover }}
                  >
                    <div className="font-medium mb-1" style={{ color: theme.colors.textPrimary }}>
                      {stat.entity_type}
                    </div>
                    <div style={{ color: theme.colors.textSecondary }}>
                      {stat.imported_fields}/{stat.total_fields} imported
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters & Actions */}
          <div 
            className="rounded-lg p-4 mb-6"
            style={{ 
              backgroundColor: theme.colors.bgCard,
              border: `1px solid ${theme.colors.borderColor}`
            }}
          >
            <div className="flex flex-col gap-4">
              {/* Top Row: Search and Actions */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search 
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                      style={{ color: theme.colors.textTertiary }}
                    />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by field name, path, description, table..."
                      className="w-full rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: theme.colors.bgHover,
                        border: `1px solid ${theme.colors.borderColor}`,
                        color: theme.colors.textPrimary
                      }}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowColumnSettings(!showColumnSettings)}
                    className="px-4 py-2 text-white rounded-lg text-sm flex items-center gap-2"
                    style={{ backgroundColor: theme.colors.primaryColor }}
                  >
                    <Settings className="w-4 h-4" />
                    <span className="hidden md:inline">Columns</span>
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="px-4 py-2 text-white rounded-lg text-sm flex items-center gap-2"
                    style={{ backgroundColor: theme.colors.successText }}
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden md:inline">Export CSV</span>
                  </button>
                </div>
              </div>

              {/* Second Row: Entity Filter and Import Filter */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <select
                  value={selectedEntity}
                  onChange={(e) => setSelectedEntity(e.target.value)}
                  className="rounded-lg px-4 py-2 text-sm"
                  style={{
                    backgroundColor: theme.colors.bgHover,
                    border: `1px solid ${theme.colors.borderColor}`,
                    color: theme.colors.textPrimary
                  }}
                >
                  <option value="all">All Entities ({totalEntities})</option>
                  {entityTypes.map(entity => (
                    <option key={entity} value={entity}>
                      {entity} ({fields.filter(f => f.entity_type === entity).length})
                    </option>
                  ))}
                </select>

                <label className="flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer"
                  style={{ backgroundColor: theme.colors.bgHover, border: `1px solid ${theme.colors.borderColor}` }}
                >
                  <input
                    type="checkbox"
                    checked={showImportedOnly}
                    onChange={(e) => setShowImportedOnly(e.target.checked)}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: theme.colors.primaryColor }}
                  />
                  <span className="text-sm" style={{ color: theme.colors.textPrimary }}>
                    Show only imported fields
                  </span>
                </label>
              </div>

              {/* Column Visibility Settings */}
              {showColumnSettings && (
                <div 
                  className="rounded-lg p-4"
                  style={{ 
                    backgroundColor: theme.colors.bgHover,
                    border: `1px solid ${theme.colors.borderColor}`
                  }}
                >
                  <div className="text-sm font-medium mb-3" style={{ color: theme.colors.textPrimary }}>
                    Show/Hide Columns
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {Object.entries({
                      entity: 'Entity',
                      table: 'DB Table',
                      column: 'DB Column',
                      edgeFunction: 'Edge Function',
                      fieldPath: 'Field Path',
                      jsonType: 'JSON Type',
                      pushPull: 'Push/Pull',
                      description: 'Description',
                      imported: 'Imported',
                      notes: 'Notes'
                    }).map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={columnVisibility[key as keyof ColumnVisibility]}
                          onChange={() => toggleColumn(key as keyof ColumnVisibility)}
                          className="w-4 h-4 rounded"
                          style={{ accentColor: theme.colors.primaryColor }}
                        />
                        <span className="text-sm" style={{ color: theme.colors.textPrimary }}>
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-sm" style={{ color: theme.colors.textSecondary }}>
                Showing {filteredFields.length} of {totalFields} fields
              </div>
            </div>
          </div>

          {/* Fields Table */}
          {filteredFields.length === 0 ? (
            <div 
              className="rounded-lg p-12 text-center"
              style={{ 
                backgroundColor: theme.colors.bgCard,
                border: `1px solid ${theme.colors.borderColor}`
              }}
            >
              <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: theme.colors.textTertiary }} />
              <p style={{ color: theme.colors.textSecondary }}>No fields found matching your filters</p>
            </div>
          ) : (
            <div 
              className="rounded-lg overflow-hidden"
              style={{ 
                backgroundColor: theme.colors.bgCard,
                border: `1px solid ${theme.colors.borderColor}`
              }}
            >
              <div className="overflow-x-auto" style={{ maxHeight: '70vh' }}>
                <table className="w-full" style={{ minWidth: '1400px' }}>
                  <thead style={{ backgroundColor: theme.colors.bgHover, position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textSecondary, width: '60px' }}>
                        Edit
                      </th>
                      {columnVisibility.entity && (
                        <th className="px-3 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textSecondary, minWidth: '120px' }}>
                          Entity
                        </th>
                      )}
                      {columnVisibility.table && (
                        <th className="px-3 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textSecondary, minWidth: '150px' }}>
                          DB Table
                        </th>
                      )}
                      {columnVisibility.column && (
                        <th className="px-3 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textSecondary, minWidth: '150px' }}>
                          DB Column
                        </th>
                      )}
                      {columnVisibility.edgeFunction && (
                        <th className="px-3 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textSecondary, minWidth: '180px' }}>
                          Edge Function
                        </th>
                      )}
                      {columnVisibility.fieldPath && (
                        <th className="px-3 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textSecondary, minWidth: '180px' }}>
                          Field Path
                        </th>
                      )}
                      {columnVisibility.jsonType && (
                        <th className="px-3 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textSecondary, minWidth: '100px' }}>
                          Type
                        </th>
                      )}
                      {columnVisibility.pushPull && (
                        <th className="px-3 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textSecondary, minWidth: '80px' }}>
                          Push/Pull
                        </th>
                      )}
                      {columnVisibility.description && (
                        <th className="px-3 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textSecondary, minWidth: '250px' }}>
                          Description
                        </th>
                      )}
                      {columnVisibility.imported && (
                        <th className="px-3 py-3 text-center text-xs font-medium uppercase" style={{ color: theme.colors.textSecondary, minWidth: '80px' }}>
                          Imported
                        </th>
                      )}
                      {columnVisibility.notes && (
                        <th className="px-3 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textSecondary, minWidth: '250px' }}>
                          Notes
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFields.map((field) => {
                      const isEditing = editingField === field.id;
                      return (
                        <tr 
                          key={field.id}
                          className="transition-colors"
                          style={{ borderTop: `1px solid ${theme.colors.borderColor}` }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <td className="px-3 py-3">
                            {isEditing ? (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => saveEditing(field.id)}
                                  className="p-1 rounded hover:bg-green-500/20"
                                  title="Save"
                                  disabled={updateFieldMutation.isPending}
                                >
                                  {updateFieldMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: theme.colors.successText }} />
                                  ) : (
                                    <Check className="w-4 h-4" style={{ color: theme.colors.successText }} />
                                  )}
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  className="p-1 rounded hover:bg-red-500/20"
                                  title="Cancel"
                                  disabled={updateFieldMutation.isPending}
                                >
                                  <X className="w-4 h-4" style={{ color: theme.colors.dangerText }} />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEditing(field)}
                                className="p-1 rounded hover:bg-blue-500/20"
                                title="Edit description and notes"
                              >
                                <Edit2 className="w-4 h-4" style={{ color: theme.colors.primaryColor }} />
                              </button>
                            )}
                          </td>
                          {columnVisibility.entity && (
                            <td className="px-3 py-3 text-sm font-medium" style={{ color: theme.colors.textPrimary }}>
                              {field.entity_type}
                            </td>
                          )}
                          {columnVisibility.table && (
                            <td className="px-3 py-3">
                              <code 
                                className="text-xs px-2 py-1 rounded font-mono"
                                style={{ 
                                  color: field.database_table ? theme.colors.successText : theme.colors.dangerText,
                                  backgroundColor: theme.colors.bgHover
                                }}
                              >
                                {field.database_table || 'N/A'}
                              </code>
                            </td>
                          )}
                          {columnVisibility.column && (
                            <td className="px-3 py-3">
                              <code 
                                className="text-xs px-2 py-1 rounded font-mono"
                                style={{ 
                                  color: field.database_column ? theme.colors.warningText : theme.colors.dangerText,
                                  backgroundColor: theme.colors.bgHover
                                }}
                              >
                                {field.database_column || 'N/A'}
                              </code>
                            </td>
                          )}
                          {columnVisibility.edgeFunction && (
                            <td className="px-3 py-3">
                              <span 
                                className="inline-flex px-2 py-1 text-xs font-medium rounded"
                                style={{ 
                                  backgroundColor: field.edge_function 
                                    ? `${theme.colors.primaryColor}20` 
                                    : `${theme.colors.textTertiary}20`,
                                  color: field.edge_function 
                                    ? theme.colors.primaryColor
                                    : theme.colors.textTertiary
                                }}
                              >
                                {field.edge_function || 'None'}
                              </span>
                            </td>
                          )}
                          {columnVisibility.fieldPath && (
                            <td className="px-3 py-3">
                              <code 
                                className="text-xs px-2 py-1 rounded"
                                style={{ 
                                  color: theme.colors.primaryColor,
                                  backgroundColor: theme.colors.bgHover
                                }}
                                title={field.field_path}
                              >
                                {field.field_name}
                              </code>
                            </td>
                          )}
                          {columnVisibility.jsonType && (
                            <td className="px-3 py-3 text-xs" style={{ color: theme.colors.textSecondary }}>
                              {field.json_type || '-'}
                            </td>
                          )}
                          {columnVisibility.pushPull && (
                            <td className="px-3 py-3">
                              <span 
                                className="inline-flex px-2 py-1 text-xs rounded"
                                style={{ 
                                  backgroundColor: theme.colors.bgHover,
                                  color: theme.colors.textPrimary
                                }}
                              >
                                {field.push_pull || '-'}
                              </span>
                            </td>
                          )}
                          {columnVisibility.description && (
                            <td className="px-3 py-3">
                              {isEditing ? (
                                <textarea
                                  value={editValues.description}
                                  onChange={(e) => setEditValues(prev => ({ ...prev, description: e.target.value }))}
                                  className="w-full rounded px-2 py-1 text-sm"
                                  rows={2}
                                  style={{
                                    backgroundColor: theme.colors.bgHover,
                                    border: `1px solid ${theme.colors.borderColor}`,
                                    color: theme.colors.textPrimary
                                  }}
                                />
                              ) : (
                                <span className="text-sm" style={{ color: theme.colors.textSecondary }} title={field.description || undefined}>
                                  {field.description || '-'}
                                </span>
                              )}
                            </td>
                          )}
                          {columnVisibility.imported && (
                            <td className="px-3 py-3 text-center">
                              <span 
                                className="inline-flex px-2 py-1 text-xs font-medium rounded"
                                style={{ 
                                  backgroundColor: field.is_imported 
                                    ? `${theme.colors.successText}20` 
                                    : `${theme.colors.textTertiary}20`,
                                  color: field.is_imported 
                                    ? theme.colors.successText
                                    : theme.colors.textTertiary
                                }}
                              >
                                {field.is_imported ? 'Yes' : 'No'}
                              </span>
                            </td>
                          )}
                          {columnVisibility.notes && (
                            <td className="px-3 py-3">
                              {isEditing ? (
                                <textarea
                                  value={editValues.notes}
                                  onChange={(e) => setEditValues(prev => ({ ...prev, notes: e.target.value }))}
                                  className="w-full rounded px-2 py-1 text-sm"
                                  rows={2}
                                  style={{
                                    backgroundColor: theme.colors.bgHover,
                                    border: `1px solid ${theme.colors.borderColor}`,
                                    color: theme.colors.textPrimary
                                  }}
                                />
                              ) : (
                                <span className="text-sm" style={{ color: theme.colors.warningText }} title={field.notes || undefined}>
                                  {field.notes || '-'}
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SuccessWareFieldCatalogPage;
